import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../atoms/Button";
import { RatingDisplay } from "./RatingDisplay";
import type { ReviewFormProps } from "../types/ReviewForm.types";

export const ReviewForm: React.FC<ReviewFormProps> = ({
  existingReview,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [comment, setComment] = useState(existingReview?.comment || "");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (rating === 0) {
      setError(t("reviewForm.errors.ratingRequired"));
      return;
    }

    if (comment.trim().length < 10) {
      setError(t("reviewForm.errors.minCharacters"));
      return;
    }

    try {
      await onSubmit({ rating, comment: comment.trim() });
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("reviewForm.errors.submitFailed");
      setError(errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Rating Selection */}
      <div>
        <label className="block text-sm font-medium text-tiber-900 mb-2">
          {t("reviewForm.ratingLabel")} <span className="text-red-500">*</span>
        </label>
        <RatingDisplay
          rating={rating}
          size="lg"
          showNumber={false}
          readonly={false}
          onClick={setRating}
        />
        {rating > 0 && (
          <p className="text-sm text-tiber-600 mt-2">
            {rating === 5 && t("reviewForm.ratingMessages.excellent")}
            {rating === 4 && t("reviewForm.ratingMessages.veryGood")}
            {rating === 3 && t("reviewForm.ratingMessages.good")}
            {rating === 2 && t("reviewForm.ratingMessages.couldBeBetter")}
            {rating === 1 && t("reviewForm.ratingMessages.needsImprovement")}
          </p>
        )}
      </div>

      {/* Comment */}
      <div>
        <label
          htmlFor="comment"
          className="block text-sm font-medium text-tiber-900 mb-2"
        >
          {t("reviewForm.commentLabel")} <span className="text-red-500">*</span>
        </label>
        <textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={5}
          className="w-full px-3 py-2 border border-tiber-300 rounded-lg
                   focus:ring-2 focus:ring-tiber-500 focus:border-transparent
                   text-tiber-900 placeholder-tiber-400
                   disabled:bg-tiber-50 disabled:cursor-not-allowed"
          placeholder={t("reviewForm.commentPlaceholder")}
          disabled={isSubmitting}
        />
        <p className="text-xs text-tiber-500 mt-1">
          {t("reviewForm.commentHelper")}
        </p>
        {comment.length > 0 && comment.length < 10 && (
          <p className="text-xs text-red-500 mt-1">
            {t("reviewForm.charactersNeeded", { count: 10 - comment.length })}
          </p>
        )}
      </div>

      {/* Tips */}
      <div className="bg-tiber-50 border border-tiber-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-tiber-900 mb-2">
          {t("reviewForm.tips.title")}
        </h4>
        <ul className="text-xs text-tiber-700 space-y-1 list-disc list-inside">
          <li>{t("reviewForm.tips.specific")}</li>
          <li>{t("reviewForm.tips.legalMatter")}</li>
          <li>{t("reviewForm.tips.communication")}</li>
          <li>{t("reviewForm.tips.honest")}</li>
        </ul>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting || rating === 0 || comment.trim().length < 10}
          className="flex-1"
        >
          {isSubmitting
            ? t("reviewForm.submitting")
            : existingReview
              ? t("reviewForm.updateReview")
              : t("reviewForm.submitReview")}
        </Button>

        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
        )}
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-tiber-600">{t("reviewForm.disclaimer")}</p>
    </form>
  );
};
